import React from 'react';
import { BlankModal, Flex, ModalBody, ModalHeader, VStack, Input, Text, Box } from '@cad-arc';
import { debounce } from 'lodash';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useShortcutKeysInvoker } from './shortcut-keys-listener';
import { alphabeticalStringComparator } from '../common/sorting/comparators';
import { useApplicationType } from '../common/hooks/use-application-type';

function ShortcutKeysModalBody(): React.ReactElement {
    const modalControls = useFullscreenModalControls();
    const keyboardShortcutInvoker = useShortcutKeysInvoker();
    const { isFirstResponder } = useApplicationType();
    const shortcutKeyViews = useBootstrapState().shortcutKeyViews;
    const dispatcherShortcutKeys = React.useMemo(() => {
        if (isFirstResponder) {
            return shortcutKeyViews.filter(
                (shortcutKeyView) => shortcutKeyView.shortcutContext === 'FIRST_RESPONDER'
            );
        }
        return shortcutKeyViews.filter(
            (shortcutKeyView) =>
                shortcutKeyView.shortcutContext === 'DISPATCHER' ||
                shortcutKeyView.shortcutContext === 'DISPATCHER_EVENT_PANEL' ||
                shortcutKeyView.shortcutContext === 'DISPATCHER_EVENT_WINDOW'
        );
    }, [shortcutKeyViews, isFirstResponder]);
    const [filter, setFilter] = React.useState<string>('');
    const debouncedSetFilter = React.useMemo(() => {
        return debounce((nextValue: string) => {
            setFilter(nextValue);
        }, 100);
    }, [setFilter]);
    const filteredShortcutKeys = React.useMemo(() => {
        return dispatcherShortcutKeys.filter((shortcutKeyView) => {
            return (
                shortcutKeyView.shortcutCommandHumanReadableName
                    .toLocaleLowerCase()
                    .indexOf(filter.toLocaleLowerCase()) !== -1
            );
        });
    }, [filter, dispatcherShortcutKeys]);
    return (
        <VStack>
            <Input
                autoFocus
                placeholder="type to filter..."
                onChange={(e) => {
                    debouncedSetFilter(e.target.value);
                }}
            />
            <VStack w="100%" h="310px" overflow="auto" spacing={1}>
                {filteredShortcutKeys
                    .sort((left, right) => {
                        return alphabeticalStringComparator(
                            left.shortcutCommandHumanReadableName,
                            right.shortcutCommandHumanReadableName
                        );
                    })
                    .map((shortcutKeyView) => (
                        <Flex
                            borderRadius="md"
                            data-testid={`${shortcutKeyView.shortcutCommand}-shortcut-list-item`}
                            justifyContent="space-between"
                            key={shortcutKeyView.shortcutCommand}
                            w="100%"
                            cursor="pointer"
                            _hover={{
                                backgroundColor: 'var(--arc-colors-interactive-hover)',
                            }}
                            onClick={() => {
                                modalControls.close();
                                keyboardShortcutInvoker(shortcutKeyView);
                            }}
                            py="half"
                        >
                            <Box ml={2} flexGrow={1}>
                                <Text variant="bodyMd">
                                    {shortcutKeyView.shortcutCommandHumanReadableName}
                                </Text>
                            </Box>
                            <Box mr={2}>
                                <Text variant="bodyMd" sx={{ fontWeight: 'bold' }}>
                                    {shortcutKeyView.shortcutKeyCombination.toUpperCase()}
                                </Text>
                            </Box>
                        </Flex>
                    ))}
            </VStack>
        </VStack>
    );
}

export type ShortcutKeysModalType = typeof ShortcutKeysModal;
export function ShortcutKeysModal(): React.ReactElement {
    const modalControls = useFullscreenModalControls();
    return (
        <BlankModal isOpen onClose={() => modalControls.close()}>
            <ModalHeader>Shortcut Keys</ModalHeader>
            <ModalBody>
                <ShortcutKeysModalBody />
            </ModalBody>
        </BlankModal>
    );
}
